import { type ReactNode, useState } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/admin/coaching/coaching-dashboard/shared/tooltip'
import { MOCK_LIBRARY_ITEMS } from '../../_mock-data/library'
import { useCoachingStore } from '../../store'
import type { Week, WeekSprintGap } from '../_lib/auto-place'
import { formatMinutesDuration } from '../_lib/duration'
import type { CurriculumItemType } from '../_lib/filter-engine'
import { type CustomCardDraft, CustomCardForm } from './custom-card-form'
import { LinkQuestionModal } from './link-question-modal'
import type { ScheduleCardActions } from './schedule-card'
import { WeekView } from './week'

export interface ScheduleProps {
  onOpenReview: () => void
  /** Opens the card-details modal (LG-360 §1); the tab above owns which card. */
  onOpenDetails?: (cardId: string) => void
}

interface CustomTarget {
  weekId: string
  dayIdx: number
  label: string
}

export function Schedule(props: ScheduleProps): ReactNode {
  const { onOpenReview, onOpenDetails } = props
  const { curriculum } = useCoachingStore()
  const [showEarlier, setShowEarlier] = useState(false)
  const [deletingWeekId, setDeletingWeekId] = useState<string | null>(null)
  const [customTarget, setCustomTarget] = useState<CustomTarget | null>(null)
  const [linkCardId, setLinkCardId] = useState<string | null>(null)
  const [linkQuery, setLinkQuery] = useState('')

  const earlierCount = curriculum.weeks.filter((week) => week.folded).length
  const indexedWeeks = curriculum.weeks.map((week, weekIdx) => ({
    week,
    weekIdx,
  }))
  const visibleWeeks = indexedWeeks.filter(
    ({ week }) => !week.folded || showEarlier,
  )
  // A Sprint the client sat out is a fact about the plan, not about the week
  // that happens to follow it: when that week is folded away, its gap rows
  // move down to the first week still on screen rather than disappearing.
  // Folded weeks are not always a prefix -- a coach can fold any elapsed
  // week -- so gaps left over past the last visible week attach after it,
  // and with every week folded they stand on their own above the list.
  const gapsBefore = new Map<string, WeekSprintGap[]>()
  const gapsAfter = new Map<string, WeekSprintGap[]>()
  let carried: WeekSprintGap[] = []
  let lastVisibleId: string | null = null
  for (const { week } of indexedWeeks) {
    const own = week.sprint?.gapsBefore ?? []
    if (week.folded && !showEarlier) {
      carried = [...carried, ...own]
      continue
    }
    gapsBefore.set(week.id, [...carried, ...own])
    carried = []
    lastVisibleId = week.id
  }
  if (lastVisibleId !== null) {
    if (carried.length > 0) gapsAfter.set(lastVisibleId, carried)
    carried = []
  }
  const orphanGaps = carried

  const closeLinkModal = (): void => {
    setLinkCardId(null)
    setLinkQuery('')
  }

  const { switchCardSource, toggleWeekFold } = curriculum
  const cardActions: ScheduleCardActions = {
    onRemove: curriculum.removeFromDay,
    onConvertToAiMock: curriculum.convertToAiMock,
    onRevertAiMock: curriculum.revertAiMock,
    onOpenLink: (cardId) => {
      setLinkCardId(cardId)
      setLinkQuery('')
    },
    onOpenReview,
    onOpenDetails,
    targetCompanies: curriculum.targetCompanies,
    onSetTargetCompany: curriculum.setAssignmentTargetCompany,
    onSwitchSource: switchCardSource
      ? (cardId, sourceId) => switchCardSource({ cardId, sourceId })
      : undefined,
  }

  const addCustom = (draft: CustomCardDraft): void => {
    if (!customTarget) return
    const weekIdx = curriculum.weeks.findIndex(
      (week) => week.id === customTarget.weekId,
    )
    if (weekIdx < 0) {
      setCustomTarget(null)
      return
    }
    const title = draft.title.trim() || 'Custom task'
    const link = draft.link.trim()
    curriculum.addCustomCard(weekIdx, customTarget.dayIdx, {
      title,
      // No assertion: `CustomCardType` is already a subset of
      // `CurriculumItemType`, so this assigns. An `as` here would keep
      // compiling if that stopped being true, and the schedule's
      // type-keyed colour and label maps would start returning undefined.
      type: draft.type,
      // No "~": the coach set this to the minute, so it is not an estimate
      // the way a derived one is.
      meta: `✎ custom · ${formatMinutesDuration(draft.estimatedMinutes)}`,
      estimatedMinutes: draft.estimatedMinutes,
      ...(link ? { link } : {}),
      ...(draft.details ? { instructions: draft.details } : {}),
    })
    setCustomTarget(null)
  }

  return (
    <section aria-labelledby="curriculum-schedule-heading" className="min-w-0">
      <div className="mb-3 flex flex-wrap items-center gap-2.5">
        <h2
          id="curriculum-schedule-heading"
          className="text-sm font-bold text-(--cd-text)"
        >
          Schedule
        </h2>
        <p className="text-[11px] text-(--cd-text-faint)">
          Hour budgets sit next to each date and carry over week to week.
          Over-budget days turn red.
        </p>
        <div className="ml-auto flex gap-2">
          {curriculum.planHistoryHasMore ? (
            <button
              aria-label="Load earlier plan dates"
              className="rounded-full border border-(--cd-border-2) px-3.5 py-1 text-xs font-semibold text-(--cd-text-muted) disabled:cursor-not-allowed disabled:opacity-50"
              disabled={
                curriculum.planLoadingMore || !curriculum.loadEarlierPlan
              }
              onClick={curriculum.loadEarlierPlan}
              type="button"
            >
              {curriculum.planLoadingMore
                ? 'Loading plan dates…'
                : 'Load earlier plan dates'}
            </button>
          ) : null}
          {curriculum.planHasMore ? (
            <button
              aria-label="Load more plan dates"
              className="rounded-full border border-(--cd-border-2) px-3.5 py-1 text-xs font-semibold text-(--cd-text-muted) disabled:cursor-not-allowed disabled:opacity-50"
              disabled={curriculum.planLoadingMore || !curriculum.loadMorePlan}
              onClick={curriculum.loadMorePlan}
              type="button"
            >
              {curriculum.planLoadingMore
                ? 'Loading more plan dates…'
                : 'Load more plan dates'}
            </button>
          ) : null}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={curriculum.optimizeSchedule}
                disabled={curriculum.bulkActionsReady === false}
                className="rounded-full border border-(--cd-purple)/35 bg-(--cd-purple)/10 px-3.5 py-1 text-xs font-semibold text-(--cd-purple)"
              >
                ✦ Optimize schedule
              </button>
            </TooltipTrigger>
            <TooltipContent>
              {curriculum.bulkActionsReason ?? 'Optimize the draft schedule'}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={curriculum.addWeek}
                disabled={curriculum.addWeekReady === false}
                className="rounded-full border border-(--cd-border-2) px-3.5 py-1 text-xs font-semibold text-(--cd-text-muted) hover:text-(--cd-text)"
              >
                + Add week
              </button>
            </TooltipTrigger>
            <TooltipContent>
              {curriculum.addWeekReason ?? 'Add another draft week'}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
      {curriculum.placing ? (
        <div
          role="status"
          // Named so it stays distinguishable from the (always-mounted)
          // FlashNote live region in both queries and SR announcements.
          aria-label="Placement mode"
          className="mb-2.5 flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-(--cd-green)/40 bg-(--cd-green)/6 px-3 py-2 text-xs text-(--cd-green-soft)"
        >
          <span>
            Placing "<strong>{curriculum.placing.title}</strong>" — click any
            green slot on a draft week (published and past weeks are locked;
            manual placement may exceed a day's hours), or{' '}
          </span>
          <button
            type="button"
            onClick={curriculum.cancelPlacing}
            className="rounded-full border border-(--cd-border-2) px-2.5 py-0.5 text-[11px] text-(--cd-text-muted)"
          >
            cancel
          </button>
        </div>
      ) : null}

      {customTarget ? (
        // Keyed per target: the per-day "＋" buttons stay live while the form
        // is open, so retargeting is a prop change on the same instance and
        // React would preserve every field. Details is the worst of them — it
        // sits below the submit button and now reaches the client's task
        // dialog, so a paragraph written for Monday would silently ship
        // attached to Tuesday's card.
        <CustomCardForm
          key={`${customTarget.weekId}:${customTarget.dayIdx}`}
          targetLabel={customTarget.label}
          onAdd={addCustom}
          onClose={() => setCustomTarget(null)}
        />
      ) : null}

      {earlierCount > 0 ? (
        <button
          type="button"
          aria-expanded={showEarlier}
          onClick={() => setShowEarlier((current) => !current)}
          className="mb-2.5 w-full rounded-lg border border-dashed border-(--cd-border) px-3.5 py-1.5 text-left text-[11px] text-(--cd-text-faint) hover:text-(--cd-text-muted)"
        >
          {/* Decorative: screen readers announce a bare ▸ as "black
              right-pointing small triangle", and this control is now the only
              way to reach a folded week. Matches the week header's chevron,
              which keeps its glyph out of the accessible name too. */}
          <span aria-hidden="true">{showEarlier ? '▾' : '▸'}</span>{' '}
          {showEarlier
            ? 'Hide earlier weeks'
            : `Show ${earlierCount} earlier week(s)`}
        </button>
      ) : null}

      <div className="space-y-2.5 overflow-x-auto">
        {orphanGaps.map((gap) => (
          <GapNoteRow key={gap.sprintId} gap={gap} placement={null} />
        ))}
        {visibleWeeks.map(({ week, weekIdx }) => (
          <SprintContextRows
            key={week.id}
            week={week}
            gapsBefore={gapsBefore.get(week.id) ?? []}
            gapsAfter={gapsAfter.get(week.id) ?? []}
          >
            <WeekView
              week={week}
              usedHoursByDay={week.days.map((_, dayIdx) =>
                curriculum.dayUsedHours(weekIdx, dayIdx),
              )}
              placing={curriculum.placing}
              deleteConfirmationOpen={deletingWeekId === week.id}
              onToggle={() => curriculum.toggleWeek(week.id)}
              onToggleFold={
                toggleWeekFold
                  ? () => {
                      // Reveal first: with the earlier weeks hidden, folding
                      // unmounts the very button the coach just pressed —
                      // focus falls to the body and the only sign anything
                      // happened is a count changing elsewhere on the page.
                      // Revealed, the week stays put and its control flips to
                      // "Unfold week".
                      setShowEarlier(true)
                      toggleWeekFold(week.id)
                    }
                  : undefined
              }
              onPublish={() => curriculum.publishWeek(week.id)}
              onUnpublish={() => curriculum.unpublishWeek(week.id)}
              onRequestDelete={() => setDeletingWeekId(week.id)}
              onCancelDelete={() => setDeletingWeekId(null)}
              onDelete={() => {
                curriculum.deleteWeek(week.id)
                setDeletingWeekId(null)
                setCustomTarget((current) =>
                  current?.weekId === week.id ? null : current,
                )
              }}
              publishedReadOnly={curriculum.persisted === true}
              onSetBudget={(dayIdx, budget) =>
                curriculum.setBudget(weekIdx, dayIdx, budget)
              }
              onPlace={(dayIdx) => curriculum.placeAt(weekIdx, dayIdx)}
              onOpenCustom={(dayIdx) =>
                setCustomTarget({
                  weekId: week.id,
                  dayIdx,
                  label: `${week.days[dayIdx].label} · ${week.label}`,
                })
              }
              cardActions={cardActions}
            />
          </SprintContextRows>
        ))}
      </div>

      <LinkQuestionModal
        open={linkCardId !== null}
        query={linkQuery}
        items={curriculum.libraryItems ?? MOCK_LIBRARY_ITEMS}
        persisted={curriculum.persisted}
        incomplete={curriculum.browseIncomplete}
        streaming={curriculum.browseStreaming}
        onQueryChange={setLinkQuery}
        onOpenChange={(open) => {
          if (!open) closeLinkModal()
        }}
        onLink={(item) => {
          if (!linkCardId) return
          curriculum.linkAiMockQuestion(linkCardId, item)
          closeLinkModal()
        }}
      />
    </section>
  )
}

/**
 * The read-only Sprint rows the persisted board draws above a week (LG-1073):
 * one line per Sprint the client sits out before it (`gapsBefore`, which the
 * schedule has already rolled forward over any folded weeks), the
 * credit-reach divider above the first week the client cannot yet fund, and
 * a note on a week the reconcile had to leave on a Sprint the client is no
 * longer in. The rows sit outside the week's own region, so each names its
 * week: a screen reader meeting one in reading order should not have to
 * guess which region it belongs to. Nothing to draw on the mock board, which
 * has no Sprints.
 */
function SprintContextRows(props: {
  week: Week
  gapsBefore: WeekSprintGap[]
  gapsAfter: WeekSprintGap[]
  children: ReactNode
}): ReactNode {
  const { week, gapsBefore, gapsAfter, children } = props
  const sprint = week.sprint
  // One shape whatever the context: the week keeps its position among the
  // siblings, so gaining or losing a note row never remounts it (and its
  // draggables). Empty lists and null notes render nothing.
  const weekName = week.label.split(' · ')[0]
  return (
    <>
      {gapsBefore.map((gap) => (
        <GapNoteRow
          key={gap.sprintId}
          gap={gap}
          placement={{ where: 'before', weekName }}
        />
      ))}
      {sprint?.beyondReach ? (
        <p
          role="note"
          className="border-t pt-1.5 text-[10.5px] font-semibold text-(--cd-amber)"
          style={{ borderColor: 'var(--cd-amber)' }}
        >
          Beyond current credits: {weekName} will not release until the client
          tops up.
        </p>
      ) : null}
      {sprint?.leftBehind ? (
        <p
          role="note"
          className="border-t pt-1.5 text-[10.5px] font-semibold text-(--cd-amber)"
          style={{ borderColor: 'var(--cd-amber)' }}
        >
          {weekName} was left on {sprint.name}, which the client{' '}
          {sprint.leftBehind === 'skipped' ? 'skipped' : 'could not fund'}: no
          later Sprint had room. It will move on its own when one opens.
        </p>
      ) : null}
      {children}
      {gapsAfter.map((gap) => (
        <GapNoteRow
          key={gap.sprintId}
          gap={gap}
          placement={{ where: 'after', weekName }}
        />
      ))}
    </>
  )
}

/**
 * One Sprint the client sat out, named relative to a visible week when there
 * is one; with every week folded away there is none, so the row stands alone.
 */
function GapNoteRow(props: {
  gap: WeekSprintGap
  placement: { where: 'before' | 'after'; weekName: string } | null
}): ReactNode {
  const { gap, placement } = props
  return (
    <p
      role="note"
      className="rounded-lg border border-dashed px-3 py-1 text-[10.5px] text-(--cd-text-muted)"
      style={{ borderColor: 'var(--cd-border)' }}
    >
      {gap.reason === 'skipped' ? 'Skipped' : 'Unfunded'}: {gap.name}
      {placement ? `, ${placement.where} ${placement.weekName}` : null}
    </p>
  )
}
